import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { Store } from '@ngxs/store';

import { captureExceptionInSentry } from './sentry-capturer';
import { AuthState } from './app/modules/shared/state/auth/auth.state';

import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { RefreshToken } from '@vandelft/modules/shared/state/auth';
import { ShowError } from '@vandelft/modules/shared/state/error';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private store: Store) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.store.selectSnapshot(AuthState.token);

    if (!request.url.endsWith('/users/refresh') && token) {
      request = this.addToken(request, token);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 403) {
          return this.handle401Error(request, next);
        }

        if (error instanceof HttpErrorResponse) {
          this.handleHttpError(request, error);
        }

        return throwError(error);
      }),
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handleHttpError(request: HttpRequest<any>, error: HttpErrorResponse): void {
    captureExceptionInSentry(error, request);
    this.store.dispatch(new ShowError(`${error.error?.message}`, `${error.status}`));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const token = this.store.selectSnapshot(AuthState.token);

      return this.store.dispatch(new RefreshToken(token)).pipe(
        switchMap(() => {
          const newToken = this.store.selectSnapshot(AuthState.token);
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        }),
      );
    }
  }
}
